import { DateTime } from "luxon";
import { useContext, useMemo } from "react";

import { M3P_ELECTION_DECISIONS_APPROVE, M3P_TERMINATION_AUTORENEWAL } from "../constants/constants";
import { MemberEligibilityContext } from "../context";
import { usePlanYear } from "../pages";

export const useGetMedicarePrescriptionPaymentPlanFlag = (plan_year: string | number) => {
  if (typeof plan_year == "string") {
    plan_year = parseInt(plan_year, 10);
  }

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const medicarePrescriptionPaymentPlanFlags = useMemo(() => {
    return memberEligibility?.member_flags.find((flag) => flag.flag_type === "medicare_prescription_payment_plan" && flag.plan_year === plan_year);
  }, [memberEligibility, memberEligibility?.member_flags, plan_year]);

  return { flag: medicarePrescriptionPaymentPlanFlags };
};

export const useIsParticipatingInMedicarePrescriptionPlan = (plan_year: string | number) => {
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(plan_year);

  const isParticipating = useMemo(() => {
    if (!flag) {
      return false;
    }

    const election = flag?.attributes?.medicare_prescription_payment_plan_election_decision;
    const discontinuationReason = flag?.attributes?.medicare_prescription_payment_plan_participation_discontinuation_reason;
    const isOptedIn = election in M3P_ELECTION_DECISIONS_APPROVE;
    const noDiscontinuationReason = !discontinuationReason || discontinuationReason === M3P_TERMINATION_AUTORENEWAL;

    return isOptedIn && noDiscontinuationReason;
  }, [flag]);

  return { isParticipating };
};

export const useIsParticipatingNextPlanYearInMedicarePrescriptionPlan = (plan_year: string | number) => {
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(plan_year);

  const isParticipatingNextPlanYear = useMemo(() => {
    if (!flag) {
      return false;
    }

    const election = flag?.attributes?.medicare_prescription_payment_plan_election_decision;
    const discontinuationReason = flag?.attributes?.medicare_prescription_payment_plan_participation_discontinuation_reason;
    const effective_date = flag?.attributes?.medicare_prescription_payment_plan_effective_date;

    const now = new Date();
    const isNextPlanYear = effective_date > now.toISOString() && `${plan_year}` > `${now.getFullYear()}`;

    return election in M3P_ELECTION_DECISIONS_APPROVE && !discontinuationReason && isNextPlanYear;
  }, [flag]);

  return { isParticipatingNextPlanYear };
};

export const useIsPendingParticipationInMedicarePrescriptionPaymentPlan = (plan_year: string | number) => {
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(plan_year);

  const isPendingParticipation = useMemo(() => {
    if (!flag) {
      return false;
    }

    const election = flag?.attributes?.medicare_prescription_payment_plan_election_decision;
    return election === "9";
  }, [flag, plan_year]);

  return { isPendingParticipation };
};

export const useMedicarePrescriptionPaymentPlanGracePeriod = (plan_year: string | number) => {
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(plan_year);

  const isInGracePeriod = useMemo(() => {
    if (!flag) {
      return undefined;
    }

    const grace_period_indicator = flag?.attributes?.medicare_prescription_payment_plan_grace_period_indicator;
    return grace_period_indicator === "Y";
  }, [flag]);

  const gracePeriodEffectiveDate = useMemo(() => {
    if (!flag) {
      return undefined;
    }
    return flag?.attributes?.medicare_prescription_payment_plan_grace_period_effective_date;
  }, [flag]);

  const gracePeriodTerminationDate = useMemo(() => {
    if (!flag) {
      return undefined;
    }
    return flag?.attributes?.medicare_prescription_payment_plan_grace_period_termination_date;
  }, [flag]);

  return { isInGracePeriod, gracePeriodEffectiveDate, gracePeriodTerminationDate };
};

export const useIsMemberPrecludedFromParticipation = (planYear: number) => {
  const {
    memberEligibility: {
      member: { termination_date },
      group: { effective_date },
      mppp_plan_year_bounds,
    },
  } = useContext(MemberEligibilityContext);
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);
  const isPrecludedByPlan = useMemo(() => {
    return flag?.attributes?.medicare_prescription_payment_plan_election_preclusion_indicator === "Y";
  }, [flag]);

  const isInvolutarilyDisenrolled = useMemo(() => {
    const now = DateTime.now();
    const discontinuationReason = flag?.attributes?.medicare_prescription_payment_plan_participation_discontinuation_reason;
    const mpppTerminationDate = flag?.attributes?.medicare_prescription_payment_plan_termination_date;
    if (discontinuationReason && mpppTerminationDate) {
      const terminationDateTime = DateTime.fromISO(mpppTerminationDate);
      return !["1", "2", "4"].includes(discontinuationReason) && terminationDateTime.year === now.year;
    }
  }, [flag]);

  const isMemberTerminated = useMemo(() => {
    const now = DateTime.now();

    return DateTime.fromISO(termination_date).startOf("day") < now.startOf("day");
  }, [termination_date]);

  const isBeforeEffectiveDate = useMemo(() => {
    if (effective_date) {
      const effectiveDate = DateTime.fromISO(effective_date);
      if (effectiveDate.month === 1 && effectiveDate.day === 1) {
        // Can always opt-in if effective date is 1/1
        return false;
      }
      // Can only opt-in if after effective date
      return effectiveDate.diffNow("days").days > 0;
    }
  }, [effective_date]);

  const isPlanYearNotOpen = !useMemo(() => {
    for (const entry of mppp_plan_year_bounds || []) {
      if ("" + entry.plan_year == "" + planYear) {
        return entry.is_open;
      }
      return false;
    }
  }, [mppp_plan_year_bounds, planYear]);

  return {
    isBeforeEffectiveDate,
    isPrecludedByPlan,
    isInvolutarilyDisenrolled,
    isMemberTerminated,
    isPlanYearNotOpen,
  };
};

export const useGetMedicarePrescriptionPaymentPlanWrittenCommunicationPreference = () => {
  const planYear = usePlanYear();
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);

  const preference = useMemo(() => {
    if (!flag) {
      return undefined;
    }

    const pref = flag?.attributes?.medicare_prescription_payment_plan_written_communication_preference;
    if (pref === "1") {
      return "mail";
    } else if (pref === "2") {
      return "email";
    } else if (pref === "3") {
      return "both";
    } else {
      return undefined;
    }
  }, [flag]);

  return { preference };
};

export const useGetIsOptedIntoMedicarePrescriptionPaymentPlanEmailCommunications = () => {
  const planYear = usePlanYear();
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);

  const isOptedIn = useMemo(() => {
    if (!flag) {
      return undefined;
    }

    const pref = flag?.attributes?.medicare_prescription_payment_plan_email_opt_in_indicator;
    if (pref === "Y") {
      return true;
    } else {
      return false;
    }
  }, [flag]);

  return { isOptedIn };
};

export const useGetIsOptedIntoMedicarePrescriptionPaymentPlanSMSCommunications = () => {
  const planYear = usePlanYear();
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);

  const isOptedIn = useMemo(() => {
    if (!flag) {
      return undefined;
    }

    const pref = flag?.attributes?.medicare_prescription_payment_plan_sms_opt_in_indicator;
    if (pref === "Y") {
      return true;
    } else {
      return false;
    }
  }, [flag]);

  return { isOptedIn };
};

export const useGetMedicarePrescriptionPaymentPlanPhoneNumber = () => {
  const planYear = usePlanYear();
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);

  const phoneNumber = useMemo(() => {
    return flag?.attributes?.medicare_prescription_payment_plan_cell_phone_number;
  }, [flag]);

  return { phoneNumber };
};

export const useGetMedicarePrescriptionPaymentPlanEmail = () => {
  const planYear = usePlanYear();
  const { flag } = useGetMedicarePrescriptionPaymentPlanFlag(planYear);

  const email = useMemo(() => {
    return flag?.attributes?.medicare_prescription_payment_plan_preferred_email;
  }, [flag]);

  return { email };
};
